/**
 * ObjectivesEditor - Reusable component for editing optimization objectives
 *
 * Features:
 * - Quick presets for common objective configurations
 * - Card-based display with percentage weights
 * - Auto-normalization to always sum to 100%
 * - Add/remove objectives
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Trash2, Target, Zap, DollarSign, CheckCircle2, ChevronDown, Lock, Loader2 } from 'lucide-react';
import { evalFunctionApi, EvalFunctionConfigSchema } from '@/services/evalFunctionApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  normalizeWeights,
  updateWeight,
  addItem,
  removeItem,
} from './hooks/useNormalizedWeights';

export interface Objective {
  name: string;
  source: 'judge_score' | 'judge_criterion' | 'metric' | 'eval_function';
  source_key?: string;
  direction: 'maximize' | 'minimize';
  weight: number;
  target_threshold?: number;
  /** If true, this is a constraint (must pass) rather than a weighted objective */
  required?: boolean;
  /** Configuration for eval functions (variables passed to the function) */
  source_config?: Record<string, unknown>;
}

/** Eval function option with id and display name */
interface EvalFunctionOption {
  id: string;
  name: string;
}

interface ObjectivesEditorProps {
  objectives: Objective[];
  onChange: (objectives: Objective[]) => void;

  /** Available judge criteria from eval config */
  availableCriteria?: string[];
  /** Available metrics from eval config */
  availableMetrics?: string[];
  /** Available eval functions (id + display name) */
  availableEvalFunctions?: EvalFunctionOption[];

  /** Show quick presets dropdown (default: true) */
  showPresets?: boolean;

  /** Minimum number of objectives (default: 1) */
  minObjectives?: number;

  /** Whether a judge (LLM) is configured - enables judge_score and judge_criterion sources */
  hasJudge?: boolean;
  /** Whether an eval function is configured - enables eval_function source */
  hasEvalFunction?: boolean;
}

// Preset configurations
type PresetKey = 'quality' | 'quality_speed' | 'quality_cost' | 'balanced' | 'custom';

interface Preset {
  label: string;
  description: string;
  objectives: Objective[];
}

const PRESETS: Record<PresetKey, Preset> = {
  quality: {
    label: 'Quality Focused',
    description: 'Optimize for judge score only',
    objectives: [
      { name: 'quality', source: 'judge_score', direction: 'maximize', weight: 1.0 },
    ],
  },
  quality_speed: {
    label: 'Quality + Speed',
    description: '70% quality, 30% latency',
    objectives: [
      { name: 'quality', source: 'judge_score', direction: 'maximize', weight: 0.7 },
      { name: 'latency', source: 'metric', source_key: 'latency_ms', direction: 'minimize', weight: 0.3 },
    ],
  },
  quality_cost: {
    label: 'Quality + Cost',
    description: '70% quality, 30% token efficiency',
    objectives: [
      { name: 'quality', source: 'judge_score', direction: 'maximize', weight: 0.7 },
      { name: 'cost', source: 'metric', source_key: 'total_tokens', direction: 'minimize', weight: 0.3 },
    ],
  },
  balanced: {
    label: 'Balanced',
    description: '50% quality, 25% speed, 25% cost',
    objectives: [
      { name: 'quality', source: 'judge_score', direction: 'maximize', weight: 0.5 },
      { name: 'latency', source: 'metric', source_key: 'latency_ms', direction: 'minimize', weight: 0.25 },
      { name: 'cost', source: 'metric', source_key: 'total_tokens', direction: 'minimize', weight: 0.25 },
    ],
  },
  custom: {
    label: 'Custom',
    description: 'Configure your own objectives',
    objectives: [],
  },
};

// Icons for different objective types
const OBJECTIVE_ICONS: Record<string, React.ReactNode> = {
  quality: <Target className="h-4 w-4 text-blue-500" />,
  latency: <Zap className="h-4 w-4 text-yellow-500" />,
  speed: <Zap className="h-4 w-4 text-yellow-500" />,
  cost: <DollarSign className="h-4 w-4 text-green-500" />,
  tokens: <DollarSign className="h-4 w-4 text-green-500" />,
};

function getObjectiveIcon(name: string): React.ReactNode {
  const lower = name.toLowerCase();
  for (const [key, icon] of Object.entries(OBJECTIVE_ICONS)) {
    if (lower.includes(key)) return icon;
  }
  return <Target className="h-4 w-4 text-muted-foreground" />;
}

function getSourceLabel(source: Objective['source'], sourceKey?: string): string {
  switch (source) {
    case 'judge_score':
      return 'Judge score';
    case 'judge_criterion':
      return sourceKey ? `Criterion: ${sourceKey}` : 'Judge criterion';
    case 'metric':
      return sourceKey ? `Metric: ${sourceKey}` : 'Metric';
    case 'eval_function':
      return sourceKey ? `Eval: ${sourceKey}` : 'Eval function';
    default:
      return source;
  }
}

export function ObjectivesEditor({
  objectives,
  onChange,
  availableCriteria = [],
  availableMetrics = ['latency_ms', 'total_tokens', 'tool_call_count'],
  availableEvalFunctions = [],
  showPresets = true,
  minObjectives = 1,
  hasJudge = true,  // Default true for backwards compatibility
  hasEvalFunction = true,  // Default true for backwards compatibility
}: ObjectivesEditorProps) {
  const [selectedPreset, setSelectedPreset] = useState<PresetKey>('custom');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Cache for function schemas keyed by function ID
  // null = fetched but no schema, undefined = not fetched yet
  const [functionSchemas, setFunctionSchemas] = useState<Record<string, EvalFunctionConfigSchema | null>>({});
  const [loadingSchemas, setLoadingSchemas] = useState<Record<string, boolean>>({});

  // Fetch function schema when an eval function is selected
  // Using a ref to track fetched IDs to avoid dependency on state in useCallback
  const fetchedRef = React.useRef<Set<string>>(new Set());

  const fetchFunctionSchema = useCallback(async (functionId: string) => {
    if (!functionId || fetchedRef.current.has(functionId)) {
      return;
    }

    // Mark as fetched immediately to prevent duplicate requests
    fetchedRef.current.add(functionId);
    setLoadingSchemas(prev => ({ ...prev, [functionId]: true }));

    try {
      const func = await evalFunctionApi.get(functionId);
      // Store schema or null if none exists
      setFunctionSchemas(prev => ({
        ...prev,
        [functionId]: func.config_schema || null,
      }));
    } catch (err) {
      console.error('Failed to fetch function schema:', err);
      // Store null to indicate we tried but failed
      setFunctionSchemas(prev => ({ ...prev, [functionId]: null }));
    } finally {
      setLoadingSchemas(prev => ({ ...prev, [functionId]: false }));
    }
  }, []);

  // Fetch schemas for any eval_function objectives that have source_key set
  useEffect(() => {
    objectives.forEach(obj => {
      if (obj.source === 'eval_function' && obj.source_key) {
        fetchFunctionSchema(obj.source_key);
      }
    });
  }, [objectives, fetchFunctionSchema]);

  // Filter presets based on available configuration
  // Only show presets that use sources the user has configured
  const availablePresets = useMemo(() => {
    const filtered: Partial<Record<PresetKey, Preset>> = { custom: PRESETS.custom };

    for (const [key, preset] of Object.entries(PRESETS)) {
      if (key === 'custom') continue;

      // Check if all objectives in this preset use available sources
      const allSourcesAvailable = preset.objectives.every(obj => {
        if (obj.source === 'judge_score' || obj.source === 'judge_criterion') {
          return hasJudge;
        }
        if (obj.source === 'eval_function') {
          return hasEvalFunction;
        }
        return true; // metric is always available
      });

      if (allSourcesAvailable) {
        filtered[key as PresetKey] = preset;
      }
    }

    return filtered;
  }, [hasJudge, hasEvalFunction]);

  // Calculate total weight (only for non-required objectives)
  const { totalWeight, hasWeightedObjectives } = useMemo(() => {
    const weightedObjectives = objectives.filter(obj => !obj.required);
    return {
      totalWeight: weightedObjectives.reduce((sum, obj) => sum + (obj.weight || 0), 0),
      hasWeightedObjectives: weightedObjectives.length > 0,
    };
  }, [objectives]);

  // Valid if weights sum to 100% OR there are no weighted objectives (all are required)
  const isValidTotal = !hasWeightedObjectives || Math.abs(totalWeight - 1) < 0.01;

  // Detect which preset matches current objectives
  useEffect(() => {
    if (objectives.length === 0) {
      setSelectedPreset('custom');
      return;
    }

    // Check if current objectives match an available preset
    for (const [key, preset] of Object.entries(availablePresets)) {
      if (key === 'custom') continue;
      if (preset.objectives.length !== objectives.length) continue;

      const matches = preset.objectives.every((presetObj, i) => {
        const obj = objectives[i];
        return (
          obj.source === presetObj.source &&
          obj.direction === presetObj.direction &&
          Math.abs(obj.weight - presetObj.weight) < 0.01
        );
      });

      if (matches) {
        setSelectedPreset(key as PresetKey);
        return;
      }
    }

    setSelectedPreset('custom');
  }, [objectives, availablePresets]);

  const handlePresetChange = (presetKey: PresetKey) => {
    setSelectedPreset(presetKey);
    if (presetKey !== 'custom' && availablePresets[presetKey]) {
      onChange([...availablePresets[presetKey].objectives]);
    }
  };

  const handleWeightChange = (index: number, newWeightPercent: number) => {
    // Convert percentage to decimal
    const newWeight = Math.max(0, Math.min(100, newWeightPercent)) / 100;
    const updated = updateWeight(objectives, index, newWeight);
    onChange(updated);
  };

  const handleObjectiveChange = (
    index: number,
    field: keyof Objective,
    value: string | number | boolean | undefined
  ) => {
    const updated = objectives.map((obj, i) => {
      if (i !== index) return obj;

      const newObj = { ...obj, [field]: value };

      // When toggling required on, set weight to 0 and default threshold
      if (field === 'required' && value === true) {
        newObj.weight = 0;
        if (newObj.target_threshold === undefined) {
          newObj.target_threshold = 1.0; // Default to must pass (100%)
        }
      }

      return newObj;
    });
    onChange(updated);
  };

  // Update a specific config value for an objective
  const handleConfigChange = (
    index: number,
    configKey: string,
    configValue: unknown
  ) => {
    const updated = objectives.map((obj, i) => {
      if (i !== index) return obj;
      return {
        ...obj,
        source_config: {
          ...obj.source_config,
          [configKey]: configValue,
        },
      };
    });
    onChange(updated);
  };

  const handleSourceChange = (index: number, newSource: Objective['source']) => {
    const updated = objectives.map((obj, i) => {
      if (i !== index) return obj;

      const newObj = { ...obj, source: newSource };

      // Clear source_key when source changes
      if (newSource === 'judge_score') {
        newObj.source_key = '';
      }

      // Default eval_function to required=true (ground truth)
      if (newSource === 'eval_function') {
        newObj.required = true;
        newObj.weight = 0;
        if (newObj.target_threshold === undefined) {
          newObj.target_threshold = 1.0;
        }
      } else if (obj.source === 'eval_function' && newSource !== 'eval_function') {
        // Switching away from eval_function, clear required
        newObj.required = false;
      }

      return newObj;
    });
    onChange(updated);
  };

  const handleAddObjective = () => {
    const newObjective: Objective = {
      name: '',
      source: 'metric',
      source_key: availableMetrics[0] || '',
      direction: 'maximize',
      weight: 0,
    };
    const updated = addItem(objectives, newObjective);
    onChange(updated);
    setExpandedIndex(updated.length - 1);
  };

  const handleRemoveObjective = (index: number) => {
    if (objectives.length <= minObjectives) return;
    const updated = removeItem(objectives, index);
    onChange(updated);
    setExpandedIndex(null);
  };

  const handleNormalize = () => {
    // Only normalize weights for non-required objectives
    const requiredObjectives = objectives.filter(obj => obj.required);
    const weightedObjectives = objectives.filter(obj => !obj.required);

    if (weightedObjectives.length === 0) return;

    const normalized = normalizeWeights(weightedObjectives);

    // Merge back together, preserving original order
    const result = objectives.map(obj => {
      if (obj.required) return obj;
      const normalizedObj = normalized.find(n => n.name === obj.name && n.source === obj.source);
      return normalizedObj || obj;
    });

    onChange(result);
  };

  return (
    <div className="space-y-4">
      {/* Preset selector */}
      {showPresets && (
        <div className="space-y-2">
          <Label>Quick Setup</Label>
          <Select value={selectedPreset} onValueChange={(v) => handlePresetChange(v as PresetKey)}>
            <SelectTrigger>
              <SelectValue placeholder="Select a preset..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(availablePresets).map(([key, preset]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex flex-col">
                    <span>{preset.label}</span>
                    <span className="text-xs text-muted-foreground">{preset.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Objectives list */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Objectives</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddObjective}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>

        <div className="space-y-2">
          {objectives.map((obj, index) => (
            <Card key={index} className="overflow-hidden">
              <Collapsible
                open={expandedIndex === index}
                onOpenChange={(open) => setExpandedIndex(open ? index : null)}
              >
                {/* Collapsed view - summary */}
                <div className="flex items-center gap-3 p-3">
                  {getObjectiveIcon(obj.name || obj.source)}

                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {obj.name || 'Unnamed objective'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {obj.direction === 'maximize' ? '↑' : '↓'} {getSourceLabel(obj.source, obj.source_key)}
                    </div>
                  </div>

                  {/* Weight input or Required badge */}
                  {obj.required ? (
                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded text-amber-700 dark:text-amber-400">
                      <Lock className="h-3 w-3" />
                      <span className="text-xs font-medium">Required</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={Math.round(obj.weight * 100)}
                        onChange={(e) =>
                          handleWeightChange(index, parseInt(e.target.value, 10) || 0)
                        }
                        className="w-16 h-8 text-right"
                        min={0}
                        max={100}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  )}

                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <ChevronDown
                        className={`h-4 w-4 transition-transform ${
                          expandedIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </Button>
                  </CollapsibleTrigger>

                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleRemoveObjective(index)}
                    disabled={objectives.length <= minObjectives}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                {/* Expanded view - edit form */}
                <CollapsibleContent>
                  <CardContent className="pt-0 pb-3 px-3 border-t">
                    <div className="grid grid-cols-2 gap-3 pt-3">
                      {/* Name */}
                      <div className="space-y-1">
                        <Label className="text-xs">Name</Label>
                        <Input
                          value={obj.name}
                          onChange={(e) =>
                            handleObjectiveChange(index, 'name', e.target.value)
                          }
                          placeholder="e.g., quality"
                          className="h-8"
                        />
                      </div>

                      {/* Direction */}
                      <div className="space-y-1">
                        <Label className="text-xs">Direction</Label>
                        <Select
                          value={obj.direction}
                          onValueChange={(v) =>
                            handleObjectiveChange(index, 'direction', v)
                          }
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="maximize">Maximize</SelectItem>
                            <SelectItem value="minimize">Minimize</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Source */}
                      <div className="space-y-1">
                        <Label className="text-xs">Source</Label>
                        <Select
                          value={obj.source}
                          onValueChange={(v) => handleSourceChange(index, v as Objective['source'])}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {/* Show current value if it's not normally available (so user can change it) */}
                            {!hasJudge && obj.source === 'judge_score' && (
                              <SelectItem value="judge_score" className="text-destructive">Judge Score (no judge configured)</SelectItem>
                            )}
                            {!hasJudge && obj.source === 'judge_criterion' && (
                              <SelectItem value="judge_criterion" className="text-destructive">Judge Criterion (no judge configured)</SelectItem>
                            )}
                            {!hasEvalFunction && obj.source === 'eval_function' && (
                              <SelectItem value="eval_function" className="text-destructive">Eval Function (none configured)</SelectItem>
                            )}
                            {hasJudge && <SelectItem value="judge_score">Judge Score</SelectItem>}
                            {hasJudge && <SelectItem value="judge_criterion">Judge Criterion</SelectItem>}
                            <SelectItem value="metric">Metric</SelectItem>
                            {hasEvalFunction && <SelectItem value="eval_function">Eval Function</SelectItem>}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Source Key - conditional */}
                      {obj.source !== 'judge_score' && (
                        <div className="space-y-1">
                          <Label className="text-xs">
                            {obj.source === 'judge_criterion'
                              ? 'Criterion'
                              : obj.source === 'metric'
                              ? 'Metric'
                              : 'Function'}
                          </Label>
                          {obj.source === 'metric' && availableMetrics.length > 0 ? (
                            <Select
                              value={obj.source_key || ''}
                              onValueChange={(v) =>
                                handleObjectiveChange(index, 'source_key', v)
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableMetrics.map((m) => (
                                  <SelectItem key={m} value={m}>
                                    {m}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : obj.source === 'judge_criterion' && availableCriteria.length > 0 ? (
                            <Select
                              value={obj.source_key || ''}
                              onValueChange={(v) =>
                                handleObjectiveChange(index, 'source_key', v)
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableCriteria.map((c) => (
                                  <SelectItem key={c} value={c}>
                                    {c}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : obj.source === 'eval_function' && availableEvalFunctions.length > 0 ? (
                            <Select
                              value={obj.source_key || ''}
                              onValueChange={(v) =>
                                handleObjectiveChange(index, 'source_key', v)
                              }
                            >
                              <SelectTrigger className="h-8">
                                <SelectValue placeholder="Select..." />
                              </SelectTrigger>
                              <SelectContent>
                                {availableEvalFunctions.map((f) => (
                                  <SelectItem key={f.id} value={f.id}>
                                    {f.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={obj.source_key || ''}
                              onChange={(e) =>
                                handleObjectiveChange(index, 'source_key', e.target.value)
                              }
                              placeholder="Enter key..."
                              className="h-8"
                            />
                          )}
                        </div>
                      )}

                      {/* Eval Function Configuration */}
                      {obj.source === 'eval_function' && obj.source_key && (
                        (() => {
                          const schema = functionSchemas[obj.source_key];
                          const isLoading = loadingSchemas[obj.source_key];

                          if (isLoading) {
                            return (
                              <div className="col-span-2 flex items-center gap-2 text-xs text-muted-foreground py-2">
                                <Loader2 className="h-3 w-3 animate-spin" />
                                Loading function config...
                              </div>
                            );
                          }

                          // null means fetched but no schema, undefined means not fetched yet
                          if (schema === null || schema === undefined) {
                            return null;
                          }

                          if (!schema.properties || Object.keys(schema.properties).length === 0) {
                            return null;
                          }

                          const properties = schema.properties;
                          const requiredProps = schema.required || [];

                          return (
                            <div className="col-span-2 space-y-2 pt-2 mt-2 border-t border-dashed">
                              <Label className="text-xs font-medium">Function Variables</Label>
                              <div className="grid grid-cols-2 gap-2">
                                {Object.entries(properties).map(([propKey, propSchema]) => {
                                  const isRequired = requiredProps.includes(propKey);
                                  const currentValue = obj.source_config?.[propKey];

                                  return (
                                    <div key={propKey} className="space-y-1">
                                      <Label className="text-xs">
                                        {propKey}
                                        {isRequired && <span className="text-red-500 ml-0.5">*</span>}
                                      </Label>
                                      {propSchema.type === 'boolean' ? (
                                        <div className="flex items-center gap-2 h-8">
                                          <Checkbox
                                            id={`config-${index}-${propKey}`}
                                            checked={currentValue === true}
                                            onCheckedChange={(checked) =>
                                              handleConfigChange(index, propKey, checked === true)
                                            }
                                          />
                                          <Label
                                            htmlFor={`config-${index}-${propKey}`}
                                            className="text-xs font-normal"
                                          >
                                            {propSchema.description || 'Enable'}
                                          </Label>
                                        </div>
                                      ) : propSchema.enum ? (
                                        <Select
                                          value={String(currentValue ?? propSchema.default ?? '')}
                                          onValueChange={(v) => handleConfigChange(index, propKey, v)}
                                        >
                                          <SelectTrigger className="h-8">
                                            <SelectValue placeholder="Select..." />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {propSchema.enum.map((enumVal) => (
                                              <SelectItem key={String(enumVal)} value={String(enumVal)}>
                                                {String(enumVal)}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      ) : propSchema.type === 'number' || propSchema.type === 'integer' ? (
                                        <Input
                                          type="number"
                                          value={currentValue ?? propSchema.default ?? ''}
                                          onChange={(e) =>
                                            handleConfigChange(
                                              index,
                                              propKey,
                                              e.target.value ? Number(e.target.value) : undefined
                                            )
                                          }
                                          placeholder={propSchema.description || propKey}
                                          className="h-8"
                                          min={propSchema.minimum}
                                          max={propSchema.maximum}
                                        />
                                      ) : (
                                        <Input
                                          type="text"
                                          value={String(currentValue ?? propSchema.default ?? '')}
                                          onChange={(e) =>
                                            handleConfigChange(index, propKey, e.target.value || undefined)
                                          }
                                          placeholder={propSchema.description || propKey}
                                          className="h-8"
                                        />
                                      )}
                                      {propSchema.description && propSchema.type !== 'boolean' && (
                                        <p className="text-[10px] text-muted-foreground truncate">
                                          {propSchema.description}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()
                      )}

                      {/* Threshold */}
                      <div className="space-y-1">
                        <Label className="text-xs">
                          {obj.required ? 'Min Threshold' : 'Target (optional)'}
                        </Label>
                        <Input
                          type="number"
                          value={obj.target_threshold ?? ''}
                          onChange={(e) =>
                            handleObjectiveChange(
                              index,
                              'target_threshold',
                              e.target.value ? parseFloat(e.target.value) : undefined
                            )
                          }
                          placeholder={obj.required ? '1.0' : 'e.g., 0.85'}
                          className="h-8"
                          step="0.01"
                          min="0"
                          max="1"
                        />
                      </div>

                      {/* Required checkbox - full width */}
                      <div className="col-span-2 flex items-center gap-2 pt-2 border-t">
                        <Checkbox
                          id={`required-${index}`}
                          checked={obj.required ?? false}
                          onCheckedChange={(checked) =>
                            handleObjectiveChange(index, 'required', checked === true)
                          }
                        />
                        <Label
                          htmlFor={`required-${index}`}
                          className="text-sm font-normal cursor-pointer"
                        >
                          Required (must pass)
                        </Label>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {obj.required
                            ? 'Candidates failing this are rejected'
                            : 'Weighted objective for ranking'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          ))}
        </div>
      </div>

      {/* Total indicator */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-3">
          {objectives.some(o => o.required) && (
            <span className="text-muted-foreground">
              <Lock className="h-3 w-3 inline mr-1" />
              {objectives.filter(o => o.required).length} required
            </span>
          )}
          {hasWeightedObjectives && (
            <span className="text-muted-foreground">
              Weights: {Math.round(totalWeight * 100)}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!hasWeightedObjectives ? (
            <span className="text-muted-foreground text-xs">All objectives are required</span>
          ) : isValidTotal ? (
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleNormalize}
              className="h-6 text-xs"
            >
              Fix to 100%
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
